import WrapInputLabel from 'common/components/wrap-input-label';
import { Form, Formik, useFormikContext } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { IoLogoUsd } from 'react-icons/io';
import { toast } from 'react-toastify';
import {
  saveCoverageSelection,
  saveUpfrontSplitAmount,
} from 'redux/features/plans/plansSlice';
import * as Yup from 'yup';
import ApplyButtons from 'components/apply-shared/apply-buttons';
import {
  useUploadRequestUrlMutation,
  useUpdateSinglePlanMutation,
} from 'redux/api/ciosUsersApi/ciosUsersApi';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import FormError from 'common/components/form-error';
import Input from 'common/components/input';
import { extractErrorMsg } from 'utils/errors';
import {
  ApplyFormContainer,
  ApplyFormField,
  ApplyFormLayout,
} from 'common/layouts/apply-form';
import { Flex, Grid, Text } from '@chakra-ui/react';
import ApplyTitle from 'components/apply-shared/apply-title';
import ApplyContent from 'components/apply-shared/apply-content';
import {
  leaseTypeEnum,
  planStatusEnum,
  backgroundTypeEnum,
  propertyKindEnum,
} from 'common/utils/enums';
import { isPropertyBulkBilling } from 'common/utils/misc';
import {
  bedroomOptions,
  leaseCategoryOptions,
  getFilteredBedroomOptions,
} from 'common/utils/selectOptions';
import SelectInput from 'common/components/select-input';
import ContactCard from 'components/shared/contact-card';
import { toDatePickerFormat } from 'common/utils/toDatePickerFormat';
import moment from 'moment';
import { getTerm } from 'common/utils/getTerm';
import RadioCard from 'components/shared/radio-card';
import {
  FaHouseCircleCheck,
  FaMagnifyingGlassLocation,
  FaSearchLocation,
} from 'react-icons/fa6';
import { isEmpty } from 'common/utils/isEmpty';
import { useNavigate } from 'react-router-dom';
import Button from 'common/components/button';
import { MonthlyRentHelperLink } from 'components/plans/monthly-rent-helper-modal';
import { UnitLayoutHelperLink } from 'components/plans/unit-layout-helper-modal';

const LeaseTermCalculator = () => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    // This effect calculates the term whenever the start or end dates change.
    if (values.startDate && values.endDate) {
      const start = moment(values.startDate);
      const end = moment(values.endDate);
      const newTerm = getTerm(start, end);

      // Only call setFieldValue if the term has actually changed
      // to prevent an infinite render loop.
      if (newTerm !== values.term) {
        setFieldValue('term', newTerm);
      }
    }
  }, [values.startDate, values.endDate, values.term, setFieldValue]);

  return null;
};

const EndDateCalculator = () => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    // This effect calculates the end date whenever the start date or term change.
    if (
      values.startDate &&
      values.term &&
      moment(values.startDate).isValid() &&
      !values.endDate
    ) {
      const newEndDate = moment(values.startDate)
        .add(values.term, 'months')
        .subtract(1, 'day');

      const formattedEndDate = toDatePickerFormat(newEndDate.toDate());

      // Only call setFieldValue if the endDate has actually changed
      // to prevent an infinite render loop.
      if (formattedEndDate !== values.endDate) {
        setFieldValue('endDate', formattedEndDate);
      }
    }
  }, [values.startDate, values.term, values.endDate, setFieldValue]);

  return null;
};

const PlanStepLeaseDetails = ({ onBackClick, onNextClick, planData }) => {
  // Simplified logic: if signDate exists, user has signed lease
  const initialSigningState = !isEmpty(planData?.lease?.signDate);
  const [hasSignedLease, setHasSignedLease] = useState(initialSigningState);
  const navigate = useNavigate();

  const isSignDatePreset = !isEmpty(planData?.lease?.signDate);

  const dispatch = useDispatch();
  // HOOKS
  const [updatePlan, { isLoading: updatePlanLoading }] =
    useUpdateSinglePlanMutation();
  const [uploadRequestUrl] = useUploadRequestUrlMutation();
  const {
    trackFormSubmission,
    trackFormSubmissionSuccess,
    trackFormSubmissionError,
    trackCustomEvent,
    trackPageLoad,
  } = useUserTracker();

  // SIDE EFFECTS
  useEffect(() => {
    trackPageLoad('PlanStepLeaseDetails');
  }, []);

  // Memo for dates
  const sevenDaysAgo = useMemo(() => {
    return moment().subtract(7, 'days').startOf('day').toDate();
  }, []);

  // Initialize property values
  const property = planData?.property;
  const lease = planData?.lease;

  return (
    <Formik
      enableReinitialize
      initialValues={{
        currency: property?.paymentInformation?.currency || 'USD',
        monthlyRent: lease?.monthlyRent,
        category: lease?.category,
        signDate: toDatePickerFormat(lease?.signDate),
        startDate: toDatePickerFormat(lease?.startDate),
        endDate: toDatePickerFormat(lease?.endDate),
        numBeds: lease?.numBeds,
        term: lease?.term || 12,
        // leaseType: lease?.leaseType || leaseTypeEnum.LONG_TERM_LEASE,
      }}
      validationSchema={Yup.object().shape({
        // Always validate these fields regardless of hasSignedLease state
        currency: Yup.string(),
        monthlyRent: Yup.number()
          .required('Please enter your monthly rent amount')
          .typeError('Please enter a valid number for your monthly rent')
          .positive('Your monthly rent must be greater than $0'),
        category: Yup.string(),
        startDate: Yup.date()
          .required('Please select when your lease starts'),
        endDate: Yup.date()
          .required('Please select when your lease ends')
          .min(Yup.ref('startDate'), 'Your lease end date must be after your start date'),
        numBeds: Yup.string()
          .required('Please select the number of bedrooms')
          .test('valid-bedroom-for-property', 'Please select a valid bedroom option for this property', function(value) {
            if (!planData?.property?.rates?.underwriting?.rentalUnitBreakdown) {
              return true; // No property selected or no rates, allow any option
            }
            if (!value) {
              return true; // Let required validation handle empty values
            }
            const breakdown = planData.property.rates.underwriting.rentalUnitBreakdown;
            const unitData = breakdown[value];
            return unitData && unitData.numberOfUnits > 0 && unitData.averageRent > 0;
          }),
        term: Yup.number(),
        // Only validate signDate when hasSignedLease is true
        ...(hasSignedLease && {
          signDate: Yup.date()
            .required('Please select your lease signing date')
            .min(
              sevenDaysAgo,
              'You must purchase ReLease coverage within 7 days of signing your lease'
            ),
        }),
      })}
      onSubmit={(values) => {
        // Only send the fields that need to be updated
        // The PATCH API should merge these fields with existing plan data
        // This approach preserves other plan fields like visaExpiration, school, etc.
        // We do NOT include other plan fields here to avoid accidentally overwriting them
        let submissionValues = {
          planId: planData.id,
          status: planStatusEnum.PENDING,
          lease: {
            ...planData?.lease,
            startDate: values.startDate,
            endDate: values.endDate,
            // leaseType: values.leaseType,
            signDate: values.signDate,
            currency: values.currency,
            monthlyRent: values.monthlyRent,
            numBeds: values.numBeds,
            term: values.term,
            category: values.category,
            // Default to LONG_TERM_LEASE as month-to-month is commented out
            leaseType: leaseTypeEnum.LONG_TERM_LEASE,
          },
        };

        // Clear signDate if user hasn't signed lease
        if (hasSignedLease === false) {
          delete submissionValues?.lease?.signDate;
        }

        trackFormSubmission(submissionValues);

        updatePlan(submissionValues)
          .unwrap()
          .then((data) => {
            // Clear coverage selections as they may be invalid with new lease details
            dispatch(saveCoverageSelection({}));
            dispatch(saveUpfrontSplitAmount(null));
            trackFormSubmissionSuccess(submissionValues);
            onNextClick();
          })
          .catch((error) => {
            const errMsg = extractErrorMsg(
              error,
              'There was an error while updating your plan. Please reload the page and try again.'
            );
            toast.error(errMsg);
            trackFormSubmissionError(submissionValues, errMsg);
          });
      }}
    >
      {({
        handleBlur,
        handleChange,
        setFieldValue,
        values,
        isValid,
        dirty,
        handleSubmit,
        setFieldTouched,
        errors,
      }) => {
        const brand = planData?.property?.brand;
        const isDarkBg =
          brand?.backgroundType === backgroundTypeEnum.BACKGROUND_DARK;
        // Form is valid if all required fields are filled
        // Note: signDate is only required when hasSignedLease is true
        const validLease =
          values?.monthlyRent &&
          values?.startDate &&
          values?.endDate &&
          (hasSignedLease ? values?.signDate : true);

        return (
          <Form
            onSubmit={(e) => e.preventDefault()}
            className='flex flex-col w-full h-full'
          >
            <ApplyTitle
              title='Purchase Coverage'
              subtitle={
                isSignDatePreset
                  ? 'Please provide your lease details.'
                  : 'Have you already signed your lease for this rental?'
              }
              buttons={
                <ApplyButtons
                  loading={updatePlanLoading}
                  isValid={isValid && validLease}
                  handleSubmit={handleSubmit}
                  moveOn={!dirty}
                  onNextClick={onNextClick}
                  onBackClick={onBackClick}
                />
              }
            />
            <LeaseTermCalculator />
            <EndDateCalculator />

            {/* main content */}
            <ApplyContent>
              {planData?.property && (
                <ApplyFormLayout
                  className={`p-6 rounded-lg border relative overflow-clip ${
                    brand ? 'border-gray-200' : 'border-gray-200 bg-gray-50'
                  }`}
                  style={
                    brand?.bgColor ? { backgroundColor: brand.bgColor } : {}
                  }
                >
                  <Flex className='z-10 relative flex-col sm:flex-row gap-6 sm:gap-8 col-span-2'>
                    {brand && (
                      <img
                        src={
                          isDarkBg && brand?.logoAlt ? brand?.logo : brand?.logo
                        }
                        alt={planData?.property?.name}
                        className='max-w-[140px] max-h-[50px] object-contain'
                      />
                    )}
                    <Flex className='flex-col gap-1'>
                      <Text
                        className={`font-medium text-lg ${
                          isDarkBg ? 'text-white' : 'text-black'
                        }`}
                      >
                        ReLease Coverage at {planData?.property?.name}
                      </Text>
                      <Text
                        className={`text-base ${isDarkBg ? 'text-white opacity-60' : 'text-black opacity-60'}`}
                      >
                        {planData?.property?.address?.streetAddress1},{' '}
                        {planData?.property?.address?.city},{' '}
                        {planData?.property?.address?.state}{' '}
                        {planData?.property?.address?.postalCode}
                      </Text>
                    </Flex>
                  </Flex>
                  {brand?.backgroundHeroImage && (
                    <img
                      src={brand?.backgroundHeroImage}
                      alt={planData?.property?.name}
                      className='absolute inset-0 opacity-15 z-0 object-cover'
                    />
                  )}
                </ApplyFormLayout>
              )}
              {/* container */}
              <ApplyFormContainer>
                {!isSignDatePreset && (
                  <Grid className='grid-cols-1 md:grid-cols-2 gap-6 mb-6 -mt-3'>
                    <RadioCard
                      isSelected={hasSignedLease === true}
                      onClick={() => setHasSignedLease(true)}
                      icon={FaHouseCircleCheck}
                      label="Yes, I've already signed a lease"
                      description={
                        <span>
                          Provide the details of your signed lease. You'll need to
                          purchase coverage within 7 days of signing to be eligible.
                        </span>
                      }
                    />
                    <RadioCard
                      isSelected={hasSignedLease === false}
                      onClick={() => {
                        setHasSignedLease(false);
                        delete values.signDate;
                      }}
                      icon={FaMagnifyingGlassLocation}
                      label="No, I haven't signed a lease yet"
                      description={
                        <span>
                          <span className='text-black'>
                            Waiting until you're covered to sign?
                          </span>{' '}
                          No problem—just add the details of your lease and
                          upload a signed copy later to activate.
                        </span>
                      }
                    />
                  </Grid>
                )}

                {hasSignedLease !== null && (
                  <>
                    {/* Lease Information */}
                    <ApplyFormLayout className={isSignDatePreset ? 'pb-6' : 'py-6'}>
                      {/* Lease Category */}
                      <ApplyFormField small>
                        <WrapInputLabel isRequired>Is this a New lease or Renewal?</WrapInputLabel>
                        <SelectInput
                          options={leaseCategoryOptions}
                          value={leaseCategoryOptions.find(
                            (option) => option.value === values.category
                          )}
                          onChange={(selectedOption) => {
                            setFieldValue(
                              'category',
                              selectedOption?.value || ''
                            );
                          }}
                          name='category'
                          onBlur={handleBlur}
                          placeholder='Select lease type'
                        />
                        <FormError name='category' />
                      </ApplyFormField>

                      {/* Monthly Rent */}
                      <ApplyFormField small>
                        <WrapInputLabel isRequired>
                          {`Monthly rent (USD)`}
                        </WrapInputLabel>

                        <Input
                          Icon={IoLogoUsd}
                          placeholderText='0.00'
                          value={values.monthlyRent}
                          onChange={(event) => {
                            handleChange(event);
                            setFieldTouched('monthlyRent');
                          }}
                          onBlur={handleBlur}
                          name='monthlyRent'
                        />
                        <Text className='ml-1 text-sm text-gray-500'>
                          Your total monthly payment. <MonthlyRentHelperLink />
                        </Text>
                        <FormError name='monthlyRent' />
                      </ApplyFormField>
                      {/* Lease Signing Date */}
                      {hasSignedLease && (
                        <ApplyFormField small>
                          <WrapInputLabel isRequired>
                            Lease Signing Date
                          </WrapInputLabel>
                          <Input
                            placeholderText='Select date'
                            type='date'
                            value={values.signDate}
                            onChange={(event) => {
                              setFieldTouched('signDate');
                              handleChange(event);
                            }}
                            onBlur={handleBlur}
                            name='signDate'
                            max={toDatePickerFormat(moment().toDate())}
                            min={toDatePickerFormat(
                              moment()
                                .subtract(7, 'days')
                                .startOf('day')
                                .toDate()
                            )}
                          />
                          <Text className='text-sm text-gray-500 ml-1'>
                            To be eligible, you must purchase ReLease within 7 days of your lease signing date.
                          </Text>
                          <FormError name='signDate' />
                        </ApplyFormField>
                      )}
                    </ApplyFormLayout>
                    <ApplyFormLayout className='py-6'>                      
                      {/* Lease Term */}
                      <ApplyFormField small>
                        <WrapInputLabel isRequired>Lease Term</WrapInputLabel>
                        <Input
                          type='number'
                          name='term'
                          value={values.term}
                          onChange={(event) => {
                            handleChange(event);
                            setFieldTouched('term');
                          }}
                          onBlur={handleBlur}
                          min={6}
                          max={24}
                          suffix='months'
                          disabled={values.startDate && values.endDate}
                        />
                        <FormError name='term' />
                      </ApplyFormField>

                      {/* Lease Start Date */}
                      <ApplyFormField small>
                        <WrapInputLabel isRequired>
                          Lease Start Date
                        </WrapInputLabel>
                        <Input
                          placeholderText='Select date'
                          type='date'
                          value={values.startDate}
                          onChange={(event) => {
                            setFieldTouched('startDate');
                            handleChange(event);
                          }}
                          onBlur={handleBlur}
                          name='startDate'
                          min={
                            values.signDate ||
                            toDatePickerFormat(
                              moment()
                                .subtract(7, 'days')
                                .startOf('day')
                                .toDate()
                            )
                          }
                        />
                        <FormError name='startDate' />
                      </ApplyFormField>

                      <ApplyFormField small>
                        <WrapInputLabel isRequired>
                          Lease End Date
                        </WrapInputLabel>
                        <Input
                          placeholderText='Select date'
                          type='date'
                          value={values.endDate}
                          onChange={(event) => {
                            setFieldTouched('endDate');
                            handleChange(event);
                          }}
                          onBlur={handleBlur}
                          name='endDate'
                          min={
                            values.startDate ||
                            toDatePickerFormat(moment().toDate())
                          }
                        />
                        <FormError name='endDate' />
                      </ApplyFormField>

                      {/* Number of Bedrooms */}
                      <ApplyFormField small>
                        <WrapInputLabel isRequired>
                          {`Unit Layout`}
                        </WrapInputLabel>
                        <SelectInput
                          options={getFilteredBedroomOptions(planData?.property, values.numBeds)}
                          placeholder={planData?.property?.kind === propertyKindEnum.STUDENT 
                            ? 'Select your unit layout (entire apartment)'
                            : 'Select the number of bedrooms in your unit'
                          }
                          value={getFilteredBedroomOptions(planData?.property, values.numBeds).find(
                            (option) => option.value === values.numBeds
                          )}
                          onChange={(selectedOption) => {
                            setFieldValue('numBeds', selectedOption.value);
                          }}
                          name='numBeds'
                          isDisabled={getFilteredBedroomOptions(planData?.property, values.numBeds).length === 0}
                        />
                        <Text className='ml-1 text-sm text-gray-500'>
                          {planData?.property?.kind === propertyKindEnum.STUDENT 
                            ? <>Select the unit layout for your entire apartment. <UnitLayoutHelperLink /></>
                            : getFilteredBedroomOptions(planData?.property, values.numBeds).length > 0 
                              ? 'Available unit layouts for this property'
                              : 'No unit layouts available for this property. Please contact support.'
                          }
                        </Text>
                        <FormError name='numBeds' />
                      </ApplyFormField>
                    </ApplyFormLayout>
                  </>
                )}
              </ApplyFormContainer>

              {/* <Tracker
                values={values}
                errors={errors}
                touched={touched}
                formName='stepThree'
              /> */}
              <ContactCard
                border={false}
                className='pt-4 border-t'
                title='Need help with your purchase?'
                subtitle='Contact us via email or phone to get help with your purchase'
              />
            </ApplyContent>
          </Form>
        );
      }}
    </Formik>
  );
};

export default PlanStepLeaseDetails;
